import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Users, TrendingUp, DollarSign, AlertTriangle, Crown, Youtube, Send,
  Search, Loader2, ShieldCheck, ShieldOff, Sparkles, Filter, Download,
  Webhook, Plus, Trash2, Zap, CheckCircle2, XCircle, Mail, Eye, PlayCircle,
  CreditCard, RefreshCw,
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// FastAPI returns 4xx `detail` as either a string OR a structured object
// (e.g. 402 subscription gates). Coerce both shapes to a string so sonner
// never tries to render an object as a React child (which crashes the card).
function apiErrorMsg(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object") return d.message || d.code || fallback;
  return fallback;
}

const PLAN_COLORS = {
  free: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
  starter: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  pro: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
};

function StatCard({ icon: Icon, label, value, accent = "red", testid }) {
  const accentMap = {
    red: "text-red-300 bg-red-500/10 border-red-500/25",
    emerald: "text-emerald-300 bg-emerald-500/10 border-emerald-500/25",
    amber: "text-amber-300 bg-amber-500/10 border-amber-500/25",
    zinc: "text-zinc-300 bg-zinc-500/10 border-zinc-500/25",
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid={testid}>
      <div className="flex items-center gap-2.5 mb-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${accentMap[accent]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">{label}</div>
      </div>
      <div className="text-3xl font-bold text-white tracking-tight">{value}</div>
    </div>
  );
}

export function AdminScreen() {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState(null);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/recent-activity`),
      ]);
      setStats(s.data);
      setActivity(a.data);
    } catch (err) {
      toast.error(apiErrorMsg(err, "Couldn't load admin stats."));
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 25 };
      if (q) params.q = q;
      if (planFilter) params.plan = planFilter;
      const { data } = await axios.get(`${API}/admin/users`, { params });
      setUsers(data.users);
      setTotal(data.total);
    } catch (err) {
      toast.error(apiErrorMsg(err, "Couldn't load users."));
    } finally {
      setLoading(false);
    }
  }, [page, q, planFilter]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  const grantPro = async (uid, email) => {
    const days = parseInt(window.prompt(`Grant Pro to ${email} for how many days?`, "30") || "0", 10);
    if (!days || days < 1) return;
    try {
      await axios.post(`${API}/admin/users/${uid}/grant-pro`, { days });
      toast.success(`Granted Pro for ${days} days to ${email}`);
      loadUsers();
      loadStats();
    } catch (err) {
      toast.error(apiErrorMsg(err, "Grant failed."));
    }
  };

  const toggleSuspend = async (uid, email, isSuspended) => {
    if (!window.confirm(isSuspended
      ? `Unsuspend ${email}? They'll regain full access immediately.`
      : `Suspend ${email}? They'll be logged out and blocked from all API access.`
    )) return;
    try {
      const action = isSuspended ? "unsuspend" : "suspend";
      await axios.post(`${API}/admin/users/${uid}/${action}`);
      toast.success(`${email} ${isSuspended ? "reinstated" : "suspended"}`);
      loadUsers();
      loadStats();
    } catch (err) {
      toast.error(apiErrorMsg(err, "Action failed."));
    }
  };

  /**
   * Download a CSV of users matching the requested plan filter. We use a
   * blob URL + temp anchor so the browser save dialog fires correctly even
   * with httpOnly auth cookies in flight (a plain <a href> on a different
   * origin wouldn't carry credentials reliably).
   */
  const exportCsv = async (planScope = "paid") => {
    try {
      const { data } = await axios.get(`${API}/admin/users/export.csv`, {
        params: { plan: planScope },
        responseType: "blob",
      });
      const blob = new Blob([data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vidroi-users-${planScope}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${planScope} users`);
    } catch (err) {
      toast.error(apiErrorMsg(err, "Export failed."));
    }
  };

  const fmtMoney = (n) => `$${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate = (s) => s ? new Date(s).toLocaleString() : "—";
  const totalPages = Math.max(1, Math.ceil(total / 25));

  return (
    <div className="space-y-8" data-testid="admin-screen">
      <div>
        <h1 className="text-3xl font-bold text-white flex items-center gap-2">
          <ShieldCheck className="w-7 h-7 text-red-400" /> Admin
        </h1>
        <p className="text-zinc-400 mt-2">Internal-only — keep an eye on growth, manage users, and intervene when needed.</p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total users" value={stats?.total_users ?? "—"} accent="zinc" testid="stat-total-users" />
        <StatCard icon={TrendingUp} label="Signups · last 7d" value={stats?.new_users_7d ?? "—"} accent="red" testid="stat-signups-7d" />
        <StatCard icon={Crown} label="Paid users" value={stats?.paid_users ?? "—"} accent="amber" testid="stat-paid-users" />
        <StatCard icon={DollarSign} label="MRR (est.)" value={stats ? fmtMoney(stats.monthly_mrr_estimate) : "—"} accent="emerald" testid="stat-mrr" />
        <StatCard icon={Youtube} label="Channels connected" value={stats?.total_channels_connected ?? "—"} accent="red" testid="stat-channels" />
        <StatCard icon={Send} label="Inject pushes" value={stats?.total_inject_pushes ?? "—"} accent="red" testid="stat-inject-pushes" />
        <StatCard icon={DollarSign} label="Revenue attributed" value={stats ? fmtMoney(stats.total_revenue_attributed) : "—"} accent="emerald" testid="stat-revenue" />
        <StatCard icon={AlertTriangle} label="Suspended" value={stats?.suspended_users ?? "—"} accent="amber" testid="stat-suspended" />
      </div>

      {/* Maintenance utilities — one-shot cleanups for legacy data bugs */}
      <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="admin-maintenance-panel">
        <h2 className="text-xl font-semibold text-white mb-1">Maintenance</h2>
        <p className="text-sm text-zinc-400 mb-4">One-shot cleanups for legacy data. Safe to run repeatedly — idempotent.</p>
        <OrphanChannelsTool />
        <div className="h-3" />
        <ForceResetMyYouTubeTool />
      </section>

      {/* Outbound webhooks — fan signup / billing events to Beehiiv / SendFox / Zapier / etc. */}
      <OutboundWebhooksPanel />

      {/* Stripe plan price-ID management — pin manually-created Stripe prices */}
      <StripePlansPanel />

      {/* Per-user LLM spend tripwire — surface users who are burning margin */}
      <LlmSpendPanel />

      {/* Weekly Insights — Monday-morning Claude-generated brief for paid users */}
      <WeeklyInsightsPanel />

      {/* Users panel */}
      <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="admin-users-panel">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <h2 className="text-xl font-semibold text-white flex-1">Users</h2>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                placeholder="Search email or name…"
                className="bg-black/40 border-white/10 text-white pl-8 h-9 w-64"
                data-testid="admin-search"
              />
            </div>
            <select
              value={planFilter}
              onChange={(e) => { setPlanFilter(e.target.value); setPage(1); }}
              className="h-9 px-3 rounded-md bg-black/40 border border-white/10 text-zinc-200 text-sm"
              data-testid="admin-plan-filter"
            >
              <option value="">All plans</option>
              <option value="free">Free</option>
              <option value="starter">Starter</option>
              <option value="pro">Pro</option>
            </select>
            {/* Export to CSV — defaults to paying customers (Starter + Pro).
                Shift-click to export all instead (escape hatch for one-off exports). */}
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => exportCsv(e.shiftKey ? "all" : "paid")}
              className="h-9 px-3 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 gap-1.5"
              data-testid="admin-export-csv"
              title="Export paying customers (Starter + Pro) as CSV. Shift-click to export all users."
            >
              <Download className="w-3.5 h-3.5" />
              Export paid
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="py-16 flex items-center justify-center text-zinc-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold border-b border-white/10">
                    <th className="text-left py-2.5 pr-3">User</th>
                    <th className="text-left py-2.5 pr-3">Plan</th>
                    <th className="text-left py-2.5 pr-3">Joined</th>
                    <th className="text-left py-2.5 pr-3">YT</th>
                    <th className="text-right py-2.5 pr-3">Sales attr.</th>
                    <th className="text-right py-2.5 pr-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-b border-white/[0.05] hover:bg-white/[0.03]" data-testid={`admin-user-${u.user_id}`}>
                      <td className="py-2.5 pr-3">
                        <div className="text-white truncate max-w-[260px]">{u.email}</div>
                        {u.name && <div className="text-xs text-zinc-500 truncate max-w-[260px]">{u.name}</div>}
                        {u.suspended && (
                          <span className="text-[10px] uppercase tracking-widest text-red-300 bg-red-500/10 border border-red-500/30 rounded-full px-1.5 py-0.5 inline-block mt-1">
                            Suspended
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 pr-3">
                        <span className={`inline-block text-[11px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full border ${PLAN_COLORS[u.plan] || PLAN_COLORS.free}`}>
                          {u.plan}
                        </span>
                      </td>
                      <td className="py-2.5 pr-3 text-zinc-400 text-xs">{fmtDate(u.created_at)}</td>
                      <td className="py-2.5 pr-3 text-zinc-400 text-xs">
                        {u.youtube_connected ? <span className="text-emerald-400">{u.youtube_channel_count} ch</span> : <span className="text-zinc-600">—</span>}
                      </td>
                      <td className="py-2.5 pr-3 text-right text-zinc-300">{fmtMoney(u.sales_attributed)}</td>
                      <td className="py-2.5 pr-3 text-right">
                        <div className="inline-flex gap-1.5">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => grantPro(u.user_id, u.email)}
                            className="h-7 px-2 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 text-xs"
                            data-testid={`admin-grant-pro-${u.user_id}`}
                          >
                            <Sparkles className="w-3 h-3 mr-1" /> Pro
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleSuspend(u.user_id, u.email, u.suspended)}
                            className={`h-7 px-2 text-xs ${u.suspended
                              ? "border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
                              : "border-red-500/30 text-red-300 hover:bg-red-500/10"}`}
                            data-testid={`admin-toggle-suspend-${u.user_id}`}
                          >
                            {u.suspended ? <><ShieldCheck className="w-3 h-3 mr-1" /> Reinstate</> : <><ShieldOff className="w-3 h-3 mr-1" /> Suspend</>}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr><td colSpan={6} className="py-8 text-center text-zinc-500">No users found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between mt-4 text-sm">
              <div className="text-zinc-500">{total} user{total === 1 ? "" : "s"} · page {page} of {totalPages}</div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="border-white/10 hover:bg-white/5" data-testid="admin-prev-page">Prev</Button>
                <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="border-white/10 hover:bg-white/5" data-testid="admin-next-page">Next</Button>
              </div>
            </div>
          </>
        )}
      </section>

      {/* Recent activity */}
      {activity && (
        <div className="grid md:grid-cols-2 gap-4">
          <ActivityCard title="Latest sign-ups" rows={activity.recent_signups} render={(s) => (
            <div className="flex justify-between gap-3 py-1.5 border-b border-white/[0.04] last:border-b-0">
              <div className="min-w-0">
                <div className="text-zinc-200 truncate text-sm">{s.email}</div>
                <div className="text-[11px] text-zinc-500">{fmtDate(s.created_at)} · {s.plan}</div>
              </div>
            </div>
          )} testid="activity-signups" />
          <ActivityCard title="Latest attributed sales" rows={activity.recent_sales} render={(s) => (
            <div className="flex justify-between gap-3 py-1.5 border-b border-white/[0.04] last:border-b-0">
              <div className="min-w-0">
                <div className="text-zinc-200 text-sm">{fmtMoney(s.sale_amount)} · {s.utm_campaign || "—"}</div>
                <div className="text-[11px] text-zinc-500">{fmtDate(s.timestamp)} · via {s.ingested_via || "pixel"}</div>
              </div>
            </div>
          )} testid="activity-sales" />
        </div>
      )}
    </div>
  );
}

// Nuclear "reset MY YouTube" — wipes every YouTube-related row for the
// currently-signed-in admin. Used when a regular Disconnect can't clear a
// stale channel because the data pre-dates the `connected_via: "oauth"`
// write flag the orphan-cleanup tool relies on.
function ForceResetMyYouTubeTool() {
  const [busy, setBusy] = React.useState(false);
  const run = async () => {
    if (!window.confirm("This will fully disconnect YOUR YouTube account from VidROI and remove every channel + video row tied to your account. You can reconnect immediately afterwards. Continue?")) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/force-reset-my-youtube`);
      const p = data.purged || {};
      toast.success(`YouTube reset — purged ${p.youtube_tokens || 0} token, ${p.channels || 0} channel(s), ${p.videos || 0} video(s)`);
    } catch (e) {
      toast.error(apiErrorMsg(e, "Reset failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-xl border border-red-500/20 bg-red-500/[0.04] p-4" data-testid="force-reset-yt-tool">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-white text-sm font-medium">Force-reset YOUR YouTube connection</div>
          <div className="text-xs text-zinc-400 mt-0.5">
            Use when a stale channel won't disconnect via the normal button. Wipes only YOUR data — other users are untouched.
          </div>
        </div>
        <Button
          size="sm"
          onClick={run}
          disabled={busy}
          className="bg-red-600 hover:bg-red-500 text-white border border-red-400/30"
          data-testid="force-reset-yt-btn"
        >
          {busy ? "Resetting…" : "Reset my YouTube"}
        </Button>
      </div>
    </div>
  );
}

function ActivityCard({ title, rows, render, testid }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid={testid}>
      <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2"><Filter className="w-3.5 h-3.5 text-zinc-500" /> {title}</h3>
      {rows.length === 0 ? (
        <div className="text-zinc-500 text-sm py-4">Nothing yet.</div>
      ) : (
        <div className="space-y-1">
          {rows.map((r, i) => <div key={r.user_id + i}>{render(r)}</div>)}
        </div>
      )}
    </div>
  );
}

// One-shot cleanup tool for "ghost" YouTube channel rows — rows where the
// underlying OAuth token was deleted (by the OLD buggy disconnect endpoint)
// but the channel row was orphaned, so the dashboard kept showing it as
// connected. Run once after deploy — safe to re-run, idempotent.
function OrphanChannelsTool() {
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [cleaning, setCleaning] = React.useState(false);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/orphan-channels`);
      setPreview(data);
    } catch (e) {
      toast.error(apiErrorMsg(e, "Couldn't load orphan list"));
    } finally {
      setLoading(false);
    }
  };

  const runCleanup = async () => {
    if (!window.confirm(`Delete ${preview?.total_orphans || 0} orphan channel row(s)? This can't be undone.`)) return;
    setCleaning(true);
    try {
      const { data } = await axios.post(`${API}/admin/cleanup-orphan-channels`);
      toast.success(`Deleted ${data.deleted_count} orphan row(s) across ${data.affected_user_count} user(s)`);
      setPreview(null);
    } catch (e) {
      toast.error(apiErrorMsg(e, "Cleanup failed"));
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-black/30 p-4" data-testid="orphan-channels-tool">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-white text-sm font-medium">Orphan YouTube channel rows</div>
          <div className="text-xs text-zinc-400 mt-0.5">
            Channels marked OAuth-connected whose token was deleted (legacy disconnect bug pre-Feb 2026).
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={loadPreview}
            disabled={loading}
            className="border-white/10 hover:bg-white/5"
            data-testid="orphan-channels-preview-btn"
          >
            {loading ? "Loading…" : "Preview"}
          </Button>
          {preview && (
            <Button
              size="sm"
              onClick={runCleanup}
              disabled={cleaning || preview.total_orphans === 0}
              className="bg-red-600 hover:bg-red-500 text-white border border-red-400/30 disabled:opacity-40"
              data-testid="orphan-channels-cleanup-btn"
            >
              {cleaning ? "Cleaning…" : `Delete ${preview.total_orphans} row(s)`}
            </Button>
          )}
        </div>
      </div>
      {preview && (
        <div className="mt-3 text-xs text-zinc-400 border-t border-white/5 pt-3" data-testid="orphan-channels-preview">
          {preview.total_orphans === 0
            ? <span className="text-emerald-400">No orphans — your DB is clean.</span>
            : <>
                Found <strong className="text-white">{preview.total_orphans}</strong> orphan row(s) across <strong className="text-white">{Object.keys(preview.by_user).length}</strong> user(s).
                Sample (first {Math.min(5, preview.rows.length)}):
                <ul className="mt-2 space-y-0.5 font-mono text-[11px]">
                  {preview.rows.slice(0, 5).map((r) => (
                    <li key={`${r.user_id}-${r.channel_id}`}>· <span className="text-zinc-500">{r.user_id.slice(0, 16)}</span> → <span className="text-white">{r.channel_name}</span></li>
                  ))}
                </ul>
              </>
          }
        </div>
      )}
    </div>
  );
}



/**
 * Outbound Webhooks panel — admin-only.
 *
 * Lets the operator fan key user-lifecycle events (signed_up, trial_started,
 * subscription_active, canceled) to any 3rd-party tool that accepts an
 * incoming JSON POST (Beehiiv, SendFox, Zapier, Make, n8n…).
 *
 * Backend routes (all under /api/admin):
 *   GET    /outbound-webhooks                    → list + supported_events
 *   POST   /outbound-webhooks {url,label,events} → create
 *   DELETE /outbound-webhooks/{id}               → remove
 *   POST   /outbound-webhooks/{id}/test          → fire a synthetic user.signed_up
 */
function OutboundWebhooksPanel() {
  const [hooks, setHooks] = React.useState([]);
  const [supportedEvents, setSupportedEvents] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [creating, setCreating] = React.useState(false);
  const [testingId, setTestingId] = React.useState(null);
  const [form, setForm] = React.useState({ url: "", label: "", events: [] });

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/outbound-webhooks`);
      setHooks(data.hooks || []);
      setSupportedEvents(data.supported_events || []);
    } catch (e) {
      toast.error(apiErrorMsg(e, "Couldn't load webhooks."));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const toggleEvent = (evt) => {
    setForm((f) => ({
      ...f,
      events: f.events.includes(evt) ? f.events.filter((e) => e !== evt) : [...f.events, evt],
    }));
  };

  const create = async () => {
    if (!form.url.trim()) { toast.error("Webhook URL is required."); return; }
    if (!form.url.startsWith("http://") && !form.url.startsWith("https://")) {
      toast.error("URL must start with http:// or https://"); return;
    }
    if (form.events.length === 0) { toast.error("Pick at least one event."); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/outbound-webhooks`, {
        url: form.url.trim(),
        label: form.label.trim(),
        events: form.events,
      });
      toast.success("Webhook saved.");
      setForm({ url: "", label: "", events: [] });
      await load();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Couldn't create webhook."));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (hook) => {
    if (!window.confirm(`Delete webhook "${hook.label || hook.url}"? Subscribers will stop receiving events immediately.`)) return;
    try {
      await axios.delete(`${API}/admin/outbound-webhooks/${hook.id}`);
      toast.success("Webhook deleted.");
      await load();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Couldn't delete."));
    }
  };

  const testFire = async (hook) => {
    setTestingId(hook.id);
    try {
      const { data } = await axios.post(`${API}/admin/outbound-webhooks/${hook.id}/test`);
      if (data.ok) {
        toast.success(`Test fired — ${hook.url} responded OK.`);
      } else {
        toast.error(`Test failed — ${data.last_status || "error"}: ${data.last_error || "see hook row"}`);
      }
      await load();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Test fire failed."));
    } finally {
      setTestingId(null);
    }
  };

  const fmtDate = (s) => s ? new Date(s).toLocaleString() : "—";
  const statusBadge = (status) => {
    if (!status) return <span className="text-zinc-500 text-xs">Never fired</span>;
    if (status === "ok") return <span className="inline-flex items-center gap-1 text-emerald-300 text-xs"><CheckCircle2 className="w-3 h-3" /> OK</span>;
    return <span className="inline-flex items-center gap-1 text-red-300 text-xs"><XCircle className="w-3 h-3" /> {status}</span>;
  };

  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="admin-outbound-webhooks-panel">
      <div className="flex items-center gap-2 mb-1">
        <Webhook className="w-5 h-5 text-amber-300" />
        <h2 className="text-xl font-semibold text-white">Outbound webhooks</h2>
      </div>
      <p className="text-sm text-zinc-400 mb-5">
        Fan user-lifecycle events to Beehiiv, SendFox, Zapier, Make, or anything that accepts JSON.
        Each enabled webhook receives a POST when subscribed events fire.
      </p>

      {/* Add form */}
      <div className="rounded-xl border border-white/10 bg-black/30 p-4 mb-5" data-testid="webhook-add-form">
        <div className="grid md:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5 block">Endpoint URL</label>
            <Input
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://hooks.zapier.com/hooks/catch/..."
              className="bg-black/40 border-white/10 font-mono text-xs text-zinc-200"
              data-testid="webhook-url-input"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5 block">Label (optional)</label>
            <Input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="Beehiiv newsletter sync"
              className="bg-black/40 border-white/10 text-sm text-zinc-200"
              data-testid="webhook-label-input"
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-2 block">Subscribe to events</label>
          <div className="flex flex-wrap gap-2">
            {supportedEvents.length === 0 && <span className="text-xs text-zinc-500">Loading events…</span>}
            {supportedEvents.map((evt) => {
              const active = form.events.includes(evt);
              return (
                <button
                  key={evt}
                  type="button"
                  onClick={() => toggleEvent(evt)}
                  className={`text-xs font-mono px-2.5 py-1 rounded-md border transition-colors ${
                    active
                      ? "bg-amber-500/15 border-amber-500/40 text-amber-200"
                      : "bg-black/30 border-white/10 text-zinc-400 hover:border-white/20 hover:text-zinc-200"
                  }`}
                  data-testid={`webhook-event-toggle-${evt}`}
                >
                  {active ? "✓ " : ""}{evt}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            onClick={create}
            disabled={creating || !form.url || form.events.length === 0}
            className="bg-amber-600 hover:bg-amber-500 text-white"
            data-testid="webhook-create-btn"
          >
            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}
            Add webhook
          </Button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="py-8 flex items-center justify-center text-zinc-400">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading webhooks…
        </div>
      ) : hooks.length === 0 ? (
        <div className="text-center text-zinc-500 text-sm py-6" data-testid="webhooks-empty">
          No outbound webhooks configured yet.
        </div>
      ) : (
        <div className="space-y-2" data-testid="webhooks-list">
          {hooks.map((h) => (
            <div
              key={h.id}
              className="rounded-lg border border-white/10 bg-black/30 p-3.5"
              data-testid={`webhook-row-${h.id}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white text-sm font-medium">{h.label || "Untitled webhook"}</span>
                    {h.enabled === false && (
                      <span className="text-[10px] uppercase tracking-widest text-zinc-400 bg-zinc-500/15 border border-zinc-500/30 rounded-full px-1.5 py-0.5">disabled</span>
                    )}
                    {statusBadge(h.last_status)}
                  </div>
                  <div className="text-xs font-mono text-zinc-400 mt-1 break-all">{h.url}</div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(h.events || []).map((evt) => (
                      <span key={evt} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/30 text-amber-200">
                        {evt}
                      </span>
                    ))}
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-2 flex flex-wrap gap-x-4 gap-y-0.5">
                    <span>Fires: {h.fire_count ?? 0}</span>
                    <span>Last: {fmtDate(h.last_fired_at)}</span>
                    {h.last_error && <span className="text-red-400 truncate max-w-md">Err: {h.last_error}</span>}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => testFire(h)}
                    disabled={testingId === h.id}
                    className="h-8 px-2.5 border-amber-500/30 text-amber-300 hover:bg-amber-500/10 text-xs"
                    data-testid={`webhook-test-${h.id}`}
                  >
                    {testingId === h.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Zap className="w-3 h-3 mr-1" /> Test</>}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => remove(h)}
                    className="h-8 px-2.5 border-red-500/30 text-red-300 hover:bg-red-500/10 text-xs"
                    data-testid={`webhook-delete-${h.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}


/**
 * Weekly Insights admin panel — shows send stats, lets admin preview the
 * email content for themselves (dry-run, no actual send), trigger the
 * whole batch now (instead of waiting for the Monday 7am cron), and view
 * the recent send log.
 *
 * Production cron: point Cloudflare Workers Cron (or any scheduler) at
 *   POST /api/admin/weekly-insights/run?token=$WEEKLY_INSIGHTS_CRON_TOKEN
 * every Monday at 7am UTC. The endpoint is idempotent per ISO week.
 */
function WeeklyInsightsPanel() {
  const [stats, setStats] = React.useState(null);
  const [logs, setLogs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [previewing, setPreviewing] = React.useState(false);
  const [running, setRunning] = React.useState(false);
  const [previewHtml, setPreviewHtml] = React.useState(null);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API}/admin/weekly-insights/stats`),
        axios.get(`${API}/admin/weekly-insights/logs`),
      ]);
      setStats(s.data);
      setLogs(l.data?.logs || []);
    } catch (e) {
      toast.error(apiErrorMsg(e, "Couldn't load weekly-insights stats."));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  const preview = async () => {
    setPreviewing(true);
    try {
      const { data } = await axios.post(`${API}/admin/weekly-insights/preview`);
      setPreviewHtml(data.preview_html || "");
      toast.success(`Preview ready — subject: ${data.subject?.slice(0, 60) || "(none)"}`);
    } catch (e) {
      toast.error(apiErrorMsg(e, "Preview failed."));
    } finally {
      setPreviewing(false);
    }
  };

  const runNow = async () => {
    if (!window.confirm("Trigger the weekly-insights batch for ALL eligible paid users right now? They will receive an email. The job is idempotent per ISO week — users already sent this week will be skipped.")) return;
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/admin/weekly-insights/run`);
      toast.success(`Batch complete — sent: ${data.sent}, skipped: ${data.skipped}, failed: ${data.failed}`);
      await refresh();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Batch run failed."));
    } finally {
      setRunning(false);
    }
  };

  const fmtDate = (s) => s ? new Date(s).toLocaleString() : "—";
  const statusPill = (status) => {
    const map = {
      sent: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
      skipped: "bg-zinc-500/10 border-zinc-500/30 text-zinc-300",
      failed: "bg-red-500/10 border-red-500/30 text-red-300",
    };
    return (
      <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${map[status] || map.skipped}`}>
        {status}
      </span>
    );
  };

  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="admin-weekly-insights-panel">
      <div className="flex items-center gap-2 mb-1">
        <Mail className="w-5 h-5 text-amber-300" />
        <h2 className="text-xl font-semibold text-white">Weekly insight emails</h2>
      </div>
      <p className="text-sm text-zinc-400 mb-5">
        Every Monday 7am UTC: a 3-section Claude-generated brief mailed to every paid user.
        Empty-data users receive a setup nudge instead. One-click unsubscribe is wired up.
      </p>

      {/* Stats grid */}
      {loading ? (
        <div className="py-8 flex items-center justify-center text-zinc-400">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading stats…
        </div>
      ) : stats ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5" data-testid="weekly-insights-stats">
          <StatBox label="Sent this week" value={stats.sent_this_week} testid="wi-stat-sent-week" />
          <StatBox label="Sent all-time" value={stats.sent_total} testid="wi-stat-sent-total" />
          <StatBox label="Failed" value={stats.failed_total} accent={stats.failed_total > 0 ? "red" : "zinc"} testid="wi-stat-failed" />
          <StatBox label="Eligible users" value={stats.eligible_users} testid="wi-stat-eligible" />
          <StatBox label="Unsubscribed" value={stats.unsubscribed_users} accent="zinc" testid="wi-stat-unsub" />
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2 mb-5">
        <Button
          onClick={preview}
          disabled={previewing}
          className="bg-amber-500/15 border border-amber-500/40 text-amber-200 hover:bg-amber-500/25 hover:text-white"
          data-testid="wi-preview-btn"
        >
          {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Eye className="w-3.5 h-3.5 mr-1.5" />}
          Preview my email
        </Button>
        <Button
          onClick={runNow}
          disabled={running}
          className="bg-amber-600 hover:bg-amber-500 text-white"
          data-testid="wi-run-now-btn"
        >
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <PlayCircle className="w-3.5 h-3.5 mr-1.5" />}
          Run batch now
        </Button>
        <Button
          onClick={refresh}
          variant="ghost"
          className="text-zinc-400 hover:text-white"
          data-testid="wi-refresh-btn"
        >
          Refresh
        </Button>
      </div>

      {/* Email preview iframe */}
      {previewHtml && (
        <div className="rounded-xl border border-amber-500/20 bg-black/40 mb-5 overflow-hidden" data-testid="wi-preview-iframe-wrap">
          <div className="flex items-center justify-between px-3 py-2 border-b border-amber-500/20 bg-amber-500/[0.04]">
            <span className="text-xs text-amber-300 font-mono uppercase tracking-widest">Email preview (no send)</span>
            <button
              type="button"
              onClick={() => setPreviewHtml(null)}
              className="text-zinc-400 hover:text-white p-1"
              data-testid="wi-preview-close"
            >
              <XCircle className="w-4 h-4" />
            </button>
          </div>
          <iframe
            title="weekly-insight-preview"
            srcDoc={previewHtml}
            sandbox=""
            className="w-full h-[480px] bg-zinc-950"
          />
        </div>
      )}

      {/* Recent logs */}
      <div>
        <h3 className="text-sm font-semibold text-white mb-2">Recent send log (last 50)</h3>
        {logs.length === 0 ? (
          <div className="text-sm text-zinc-500 py-4 text-center" data-testid="wi-logs-empty">
            No sends yet. Hit "Run batch now" to trigger your first round.
          </div>
        ) : (
          <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="wi-logs-list">
            {logs.map((l, i) => (
              <div key={i} className="flex items-center gap-3 text-xs px-2.5 py-1.5 rounded bg-black/30 border border-white/5">
                {statusPill(l.status)}
                <span className="text-zinc-300 font-mono truncate flex-1">{l.email || "(no email)"}</span>
                <span className="text-zinc-500">{l.iso_week}</span>
                <span className="text-zinc-600 hidden sm:inline">{l.kind || "—"}</span>
                <span className="text-zinc-500 text-[10px]">{fmtDate(l.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function StatBox({ label, value, accent = "amber", testid }) {
  const accentMap = {
    amber: "border-amber-500/30 bg-amber-500/[0.04] text-amber-300",
    red: "border-red-500/30 bg-red-500/[0.04] text-red-300",
    zinc: "border-white/10 bg-white/[0.02] text-zinc-300",
  };
  return (
    <div className={`rounded-lg border px-3 py-2.5 ${accentMap[accent]}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</div>
      <div className="text-2xl font-bold text-white mt-0.5">{value}</div>
    </div>
  );
}


/**
 * Stripe Plans admin panel — pin a manually-created Stripe price_id to a plan.
 *
 * Backend caches the (plan_id → price_id) map in Mongo's `stripe_plans` collection.
 * On first checkout, if no row exists, VidROI auto-creates a Stripe product + price.
 * This panel lets admin OVERRIDE that with their own pre-created Stripe prices —
 * essential after switching Stripe accounts or when you want to control the
 * exact Stripe metadata (e.g. for Partnero/affiliate matching).
 *
 * Backend routes (under /api/admin):
 *   GET    /stripe-plans                       → list current mappings
 *   POST   /stripe-plans/set-price-id          → pin a price (validates with Stripe)
 *   DELETE /stripe-plans/{plan_id}             → clear (next checkout auto-creates)
 */
function StripePlansPanel() {
  const [plans, setPlans] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [draft, setDraft] = React.useState({}); // plan_id -> input value
  const [saving, setSaving] = React.useState(null); // plan_id currently saving

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/stripe-plans`);
      setPlans(data.plans || []);
      // Pre-fill drafts with current price_ids so admin can see what's set
      const d = {};
      (data.plans || []).forEach((p) => { d[p.plan_id] = p.price_id || ""; });
      setDraft(d);
    } catch (e) {
      toast.error(apiErrorMsg(e, "Couldn't load Stripe plans."));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  const save = async (plan_id) => {
    const price_id = (draft[plan_id] || "").trim();
    if (!price_id.startsWith("price_")) {
      toast.error("Price ID must start with 'price_'");
      return;
    }
    setSaving(plan_id);
    try {
      const { data } = await axios.post(`${API}/admin/stripe-plans/set-price-id`, { plan_id, price_id });
      toast.success(`Pinned ${plan_id} → ${data.price_id} ($${(data.unit_amount/100).toFixed(2)} ${data.currency.toUpperCase()})`);
      await refresh();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Couldn't save."));
    } finally {
      setSaving(null);
    }
  };

  const clear = async (plan_id) => {
    if (!window.confirm(`Clear the cached Stripe price for "${plan_id}"? Next checkout will auto-create a fresh product + price in Stripe. Existing subscribers are NOT affected.`)) return;
    try {
      await axios.delete(`${API}/admin/stripe-plans/${plan_id}`);
      toast.success(`Cleared cached price for ${plan_id}`);
      await refresh();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Couldn't clear."));
    }
  };

  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="admin-stripe-plans-panel">
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <CreditCard className="w-5 h-5 text-amber-300" />
            <h2 className="text-xl font-semibold text-white">Stripe plans</h2>
          </div>
          <p className="text-sm text-zinc-400 max-w-xl">
            Pin a manually-created Stripe price to a VidROI plan, or use &ldquo;Refresh all&rdquo; to
            auto-reconcile every plan&rsquo;s cached price against the current `PLANS[id].price_usd`
            (the weekly cron does this too — this is the manual escape hatch).
          </p>
        </div>
        <RefreshAllPricesButton onDone={refresh} />
      </div>

      {loading ? (
        <div className="py-8 flex items-center justify-center text-zinc-400">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <div className="space-y-3" data-testid="stripe-plans-list">
          {plans.map((p) => (
            <div
              key={p.plan_id}
              className={`rounded-xl border p-4 ${p.deprecated ? "border-white/5 bg-black/20 opacity-70" : "border-white/10 bg-black/30"}`}
              data-testid={`stripe-plan-row-${p.plan_id}`}
            >
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-semibold">{p.plan_name}</span>
                  <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-white/[0.06] border border-white/10 text-zinc-300 font-mono">
                    {p.plan_id}
                  </span>
                  <span className="text-amber-300 font-mono text-sm">${p.price_usd.toFixed(2)}/mo</span>
                  {p.deprecated && (
                    <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-zinc-500/10 border border-zinc-500/30 text-zinc-400">
                      deprecated alias
                    </span>
                  )}
                </div>
                {p.price_id && (
                  <button
                    type="button"
                    onClick={() => clear(p.plan_id)}
                    className="text-[11px] text-red-300 hover:text-red-200 flex items-center gap-1"
                    data-testid={`stripe-plan-clear-${p.plan_id}`}
                  >
                    <RefreshCw className="w-3 h-3" /> Reset
                  </button>
                )}
              </div>
              <div className="flex gap-2 items-center">
                <Input
                  value={draft[p.plan_id] || ""}
                  onChange={(e) => setDraft({ ...draft, [p.plan_id]: e.target.value })}
                  placeholder="price_xxxxxxxxxxxxxx"
                  className="bg-black/40 border-white/10 font-mono text-xs text-zinc-200"
                  data-testid={`stripe-plan-price-input-${p.plan_id}`}
                />
                <Button
                  onClick={() => save(p.plan_id)}
                  disabled={saving === p.plan_id || !(draft[p.plan_id] || "").trim() || (draft[p.plan_id] || "").trim() === (p.price_id || "")}
                  className="bg-amber-600 hover:bg-amber-500 text-white flex-shrink-0"
                  data-testid={`stripe-plan-save-${p.plan_id}`}
                >
                  {saving === p.plan_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
                </Button>
              </div>
              {p.product_id && (
                <div className="text-[11px] text-zinc-500 mt-2 font-mono">
                  product: {p.product_id} · updated: {p.updated_at ? new Date(p.updated_at).toLocaleString() : "—"}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * RefreshAllPricesButton — calls the audit-and-heal endpoint we built for the
 * weekly cron, gives the admin a one-click way to reconcile every plan's
 * cached Stripe price with `PLANS[id].price_usd`. Same code path as the cron,
 * so if it works here it works there.
 */
function RefreshAllPricesButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/stripe-plans/audit-and-heal`);
      const healed = data.healed || 0;
      const checked = data.checked || 0;
      const errors = data.errors || 0;
      if (errors > 0) {
        toast.error(`${errors} plan${errors === 1 ? "" : "s"} errored — check backend logs`);
      } else if (healed > 0) {
        toast.success(`Refreshed ${healed} of ${checked} plans — Stripe now matches PLANS config`);
      } else {
        toast.success(`All ${checked} plans already in sync — nothing to refresh`);
      }
      onDone?.();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Failed to audit Stripe prices"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      onClick={run}
      disabled={busy}
      variant="outline"
      className="bg-white/[0.03] border-white/10 text-zinc-200 hover:bg-white/[0.08] hover:text-white flex-shrink-0"
      data-testid="stripe-plans-refresh-all-btn"
    >
      {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Auditing…</> : <><RefreshCw className="w-4 h-4 mr-2" /> Refresh all prices</>}
    </Button>
  );
}


/**
 * LlmSpendPanel — surfaces the tripwire built into the backend: users whose
 * estimated monthly LLM variable cost has crossed the alert threshold.
 * Backend does the token→dollars maths (see /app/backend/usage.py).
 */
function LlmSpendPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/llm-spend`);
      setData(r.data);
    } catch (e) {
      toast.error(apiErrorMsg(e, "Failed to load LLM spend"));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const items = data?.top_users || [];
  const threshold = data?.threshold_usd ?? 20;

  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="admin-llm-spend-panel">
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Zap className="w-5 h-5 text-amber-300" />
            <h2 className="text-xl font-semibold text-white">LLM spend tripwire</h2>
          </div>
          <p className="text-sm text-zinc-400 max-w-xl">
            Estimated per-user variable cost this month (Ask AI + Blend Hooks + Niche Review).
            Any user over <span className="text-amber-300 font-medium">${threshold}/mo</span> shows here so you can
            spot margin-eating power users before they cost you real money.
          </p>
        </div>
        <Button
          onClick={load}
          disabled={loading}
          variant="outline"
          className="bg-white/[0.03] border-white/10 text-zinc-200 hover:bg-white/[0.08] hover:text-white flex-shrink-0"
          data-testid="admin-llm-spend-refresh"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
      </div>

      {loading ? (
        <div className="py-8 flex items-center justify-center text-zinc-400">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="py-8 text-center text-zinc-500 text-sm" data-testid="admin-llm-spend-empty">
          <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-emerald-500/60" />
          Nobody has crossed ${threshold}/mo this month. Margin is safe.
        </div>
      ) : (
        <div className="space-y-2" data-testid="admin-llm-spend-list">
          {items.map((u, i) => {
            const overBadgeColor = u.estimated_usd >= threshold * 1.5
              ? "bg-red-500/15 text-red-300 border-red-500/30"
              : "bg-amber-500/15 text-amber-300 border-amber-500/30";
            return (
              <div
                key={u.user_id}
                className="rounded-xl border border-white/10 bg-black/30 p-4 flex items-start justify-between gap-3"
                data-testid={`admin-llm-spend-row-${i}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-white font-medium truncate">{u.email || u.user_id}</span>
                    <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border font-mono ${PLAN_COLORS[u.plan] || PLAN_COLORS.free}`}>
                      {u.plan || "free"}
                    </span>
                  </div>
                  <div className="text-xs text-zinc-500 tabular-nums">
                    Ask AI: <span className="text-zinc-300">{u.ask_ai_msgs}</span> · Blends: <span className="text-zinc-300">{u.blend_hooks}</span> · Competitor: <span className="text-zinc-300">{u.competitor_analyses}</span>
                  </div>
                </div>
                <div className={`text-right rounded-lg border ${overBadgeColor} px-3 py-1.5`}>
                  <div className="text-lg font-bold tabular-nums">${u.estimated_usd.toFixed(2)}</div>
                  <div className="text-[9px] uppercase tracking-widest">est / mo</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
